import { Component } from "@angular/core";
import { DynamicFormComponent } from "../../dynamic-form/dynamic-form.component";
import { DynamicForm } from "../../../models/dynamic-form";
import { FormBuilder, FormGroup } from "@angular/forms";
import ShippingBillDynamicForm from "../config/ShippingBillDynamicForm.config.json";
import MarksAndNumberEditableTableConfig from "../config/MarksAndNumberEditableTable.config.json";
import { DynamicFormService } from "../../../services/dynamic-form.service";
import { BackButtonComponent } from "../../back-button/back-button.component";
import { PageCloseButtonComponent } from "../../common/close-button/page-close-button.component";
import { ActivatedRoute, Router } from "@angular/router";
import { MarksAndNumbersRange, ShippingBill } from "../../../models/shipping-bill.interface";
import { ToasterService } from "../../../services/toaster.service";
import { LoadingService } from "../../../services/loading.service";
import { EditableTableComponent } from "../../editable-table/editable-table.component";
import { NgIf } from "@angular/common";
import { EditableTableHeader } from "../../../models/editable-table-header.interface";
import { SvgIconComponent } from "../../svg-icon/svg-icon.component";
import { DataUpdatedOutput } from "../../editable-table/DataUpdatedOutput.interface";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { InventoryService } from "../../../services/inventory-service";

const FORM_GROUP_KEYS = {
  CONTAINER_INFO: "containerInfo",
  VESSEL_INFO: "vesselInfo",
  CARGO_INFO: "cargoInfo"
};

@Component({
  selector: "app-manage-shipping-bill",
  standalone: true,
  imports: [
    DynamicFormComponent,
    BackButtonComponent,
    PageCloseButtonComponent,
    EditableTableComponent,
    NgIf,
    SvgIconComponent
  ],
  templateUrl: "./manage-shipping-bill.component.html",
  styleUrl: "./manage-shipping-bill.component.scss"
})
export class ManageShippingBillComponent {
  containerInfoFormConfig: DynamicForm = ShippingBillDynamicForm.containerInfoFormConfig;
  vesselInfoFormConfig: DynamicForm = ShippingBillDynamicForm.vesselInfoFormConfig;
  cargoInfoFormConfig: DynamicForm = ShippingBillDynamicForm.cargoInfoFormConfig;
  marksAndNumbersEditableTableConfig: EditableTableHeader[] = MarksAndNumberEditableTableConfig;
  shippingBillForm: FormGroup;
  shippingBillId: string = "";
  readOnlyMode: boolean = false;
  editOnlyMode: boolean = false;
  marksAndNumberData: MarksAndNumbersRange[] = [];
  private tableChangeSubject = new Subject<DataUpdatedOutput>();


  constructor(private formBuilder: FormBuilder,
              private dynamicFormService: DynamicFormService,
              private router: Router,
              private inventoryService: InventoryService,
              private toasterService: ToasterService,
              private loadingService: LoadingService,
              private route: ActivatedRoute) {
    this.shippingBillForm = this.formBuilder.group({
      [FORM_GROUP_KEYS.CONTAINER_INFO]: this.formBuilder.group({}),
      [FORM_GROUP_KEYS.VESSEL_INFO]: this.formBuilder.group({}),
      [FORM_GROUP_KEYS.CARGO_INFO]: this.formBuilder.group({})
    });
  }

  ngOnInit() {
    this.readOnlyMode = this.route.snapshot.url[0].path === "view";
    this.editOnlyMode = this.route.snapshot.url[0].path === "edit";
    this.shippingBillId = this.route.snapshot.paramMap.get("shippingBillId") || "";

    this.dynamicFormService.addControlsToForm(this.containerInfoFormGroup, this.containerInfoFormConfig);
    this.dynamicFormService.addControlsToForm(this.vesselInfoFormGroup, this.vesselInfoFormConfig);
    this.dynamicFormService.addControlsToForm(this.cargoInfoFormGroup, this.cargoInfoFormConfig);

    if (this.readOnlyMode) {
      this.fetchShippingBillData();
      this.shippingBillForm.disable();
    } else if (this.editOnlyMode) {
      this.fetchShippingBillData();
    }

    //debounce the table change subject to handle range calculations
    this.tableChangeSubject
      .pipe(debounceTime(800))
      .subscribe(event => {
        const { rangeFrom, rangeTo } = event.rowInfo;

        // Type coercion and NaN check
        const from = Number(rangeFrom);
        const to = Number(rangeTo);

        if (isNaN(from) || isNaN(to)) {
          this.toasterService.error(`Row ${event.index + 1} : Both Range From and Range To must be valid numbers.`);
          return;
        }

        // Logical check that Range To > Range From
        if (to <= from) {
          this.toasterService.error(`Row ${event.index + 1} : Range To should be greater than Range From`);
          return;
        }

        // Overlap check (excluding the current row)
        const otherRanges = this.marksAndNumberData
          .filter((_, i) => i !== event.index)
          .map(data => ({
            from: Number(data.rangeFrom) || 0,
            to: Number(data.rangeTo) || 0
          }));

        const isOverlap = otherRanges.some(range =>
          !(to < range.from || from > range.to)
        );

        if (isOverlap) {
          this.toasterService.error(`Row ${event.index + 1} : Range cannot overlap with existing ranges.`);
          return;
        }

        // Update number of packages
        this.marksAndNumberData[event.index].numberOfPackages = to - from + 1;
      });
  }

  get containerInfoFormGroup(): FormGroup {
    return this.shippingBillForm.get(FORM_GROUP_KEYS.CONTAINER_INFO) as FormGroup;
  }

  get vesselInfoFormGroup(): FormGroup {
    return this.shippingBillForm.get(FORM_GROUP_KEYS.VESSEL_INFO) as FormGroup;
  }

  get cargoInfoFormGroup(): FormGroup {
    return this.shippingBillForm.get(FORM_GROUP_KEYS.CARGO_INFO) as FormGroup;
  }


  saveShippingBill() {
    this.shippingBillForm.markAllAsTouched();
    if (this.shippingBillForm.invalid) {
      return;
    }
    if (!this.validateMarksAndNumbers()) return;

    const shippingBillData: ShippingBill = this.prepareShippingBillFormData();
    console.log(shippingBillData);
    if (this.editOnlyMode) {
      this.callApiToUpdateShippingBill(shippingBillData, this.shippingBillId);
    } else {
      this.callApiToSaveShippingBill(shippingBillData);
    }
  }

  validateMarksAndNumbers(): boolean {
    const ranges = this.marksAndNumberData.map((data, index) => {
      const fromRaw = data.rangeFrom;
      const toRaw = data.rangeTo;

      // Null/empty check
      if (fromRaw == null || toRaw == null || fromRaw === "" || toRaw === "") {
        this.toasterService.error(`Row ${index + 1} : Both Range From and Range To are required.`);
        return null;
      }

      const from = Number(fromRaw);
      const to = Number(toRaw);

      if (isNaN(from) || isNaN(to)) {
        this.toasterService.error(`Row ${index + 1} : Both Range From and Range To must be valid numbers.`);
        return null;
      }

      if (to <= from) {
        this.toasterService.error(`Row ${index + 1} : Range To should be greater than Range From.`);
        return null;
      }

      return { index, from, to };
    });

    // If any row failed validation (returned null), stop further validation
    if (ranges.some(r => r === null)) return false;

    // Check for overlapping ranges
    for (let i = 0; i < ranges.length; i++) {
      const current = ranges[i]!;
      for (let j = i + 1; j < ranges.length; j++) {
        const other = ranges[j]!;

        const isOverlap = !(current.to < other.from || current.from > other.to);
        if (isOverlap) {
          this.toasterService.error(`Row ${current.index + 1} overlaps with Row ${other.index + 1}.`);
          return false;
        }
      }
    }

    //check sum of packages should not exceed total nop
    const totalPackages = this.marksAndNumberData.map((data) => data.numberOfPackages || 0).reduce((acc, val) => acc + val, 0);
    console.log(totalPackages);
    if (totalPackages > this.cargoInfoFormGroup.get("nop")?.value) {
      this.toasterService.error("Total number of packages exceeds the total NOP entered.");
      return false;
    }
    return true;
  }


  goBack() {
    this.router.navigateByUrl("/manage-operations/shipping-bill");
  }

  private prepareShippingBillFormData() {
    return {
      ...this.containerInfoFormGroup.value,
      ...this.cargoInfoFormGroup.value,
      ...this.vesselInfoFormGroup.value,
      marksAndNumbers: {
        ranges: this.marksAndNumberData
      }
    };
  }

  private callApiToSaveShippingBill(shippingBillData: ShippingBill) {
    this.inventoryService.saveShippingBill(shippingBillData).subscribe({
      next: (response) => {
        this.toasterService.success("Shipping Bill saved successfully");
        this.goBack();
      },
      error: (error) => {
        this.toasterService.error(error.error.errorDesc);
      }
    });
  }

  private fetchShippingBillData() {
    this.inventoryService.fetchShippingBillById(this.shippingBillId).subscribe({
      next: (response) => {
        const shippingBillData: ShippingBill = response.data;
        this.containerInfoFormGroup.patchValue(shippingBillData);
        this.vesselInfoFormGroup.patchValue(shippingBillData);
        this.cargoInfoFormGroup.patchValue(shippingBillData);
        this.marksAndNumberData = shippingBillData.marksAndNumbers?.ranges ?? [];
      },
      error: (error) => {
        this.toasterService.error(error.error.errorDesc);
      }
    });
  }

  private callApiToUpdateShippingBill(shippingBillData: ShippingBill, shippingBillId: string) {
    this.inventoryService.updateShippingBill(shippingBillId, shippingBillData).subscribe({
      next: (response) => {
        this.toasterService.success("Shipping Bill updated successfully");
        this.goBack();
      },
      error: (error) => {
        this.toasterService.error(error.error.errorDesc);
      }
    });
  }

  handleDataDeletion(event: any) {
    this.marksAndNumberData.splice(event.rowIndex, 1);
  }

  addNewRange() {
    this.marksAndNumberData?.push({ id: this.marksAndNumberData.length + 1, rangeFrom: "", rangeTo: "" });
  }

  handleTableDataChange(event: DataUpdatedOutput) {
    this.tableChangeSubject.next(event);
  }
}
